/**
 * @file: useSearchTasks.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { useInfiniteList } from '@euler/functions/useInfiniteList';
import { useIsFocused$ } from '@euler/functions/useIsFocused$';
import { usePersistFn } from '@euler/utils/hooks';
import { uniqBy } from 'ramda';
import { useEffect, useMemo, useRef } from 'react';
import {
  BehaviorSubject,
  debounceTime,
  distinctUntilChanged,
  filter,
  first,
  from,
  map,
  startWith,
  switchMap,
} from 'rxjs';

export type SearchParams = {
  keyword: string;
  offset: number;
  pageNo: number;
  limit: number;
};

export type SearchResponse<T> = T[];

export type SearchFunction<T> = (
  params: SearchParams,
) => Promise<SearchResponse<T>>;

const dataWithoutDuplicates = <T>(data: T[], keyFn?: (item: T) => string) => {
  if (keyFn == null) return data;
  return uniqBy(keyFn, data);
};

export const useSearchResults = <T>({
  limit,
  search,
  debounced,
  keyExtractor,
  query$,
}: {
  search: SearchFunction<T>;
  limit: number;
  debounced?: number;
  keyExtractor?: (item: T) => string;
  query$: BehaviorSubject<string>;
}) => {
  const lastKeyword = useRef<string>();

  const query = usePersistFn(async (offset: number, keyword: string) => {
    // this is provided for convenience only because most of our paginated
    // api accepts the pageNo as paging parameter instead of offset.
    // it's up to the caller to decide which one to use to perform request.
    const pageNo = Math.floor(offset / limit) + 1;

    const results = await search({
      keyword: keyword?.trim() ?? '',
      offset,
      limit,
      pageNo,
    });

    return results;
  });

  const { replace, ...rest } = useInfiniteList<T>({
    query,
    limit,
    initialLoading: true,
  });

  const data = useMemo(
    () => dataWithoutDuplicates(rest.data, keyExtractor),
    [rest.data, keyExtractor],
  );

  const isFocused$ = useIsFocused$();

  useEffect(() => {
    const sub = query$
      .pipe(
        startWith(''),
        debounceTime(debounced ?? 0),
        distinctUntilChanged(
          (prev, curr) => prev === curr || curr === lastKeyword.current,
        ),
        switchMap(keyword => {
          return isFocused$.pipe(
            filter(Boolean),
            first(),
            map(() => keyword),
          );
        }),
        switchMap(keyword => {
          return from(
            replace(keyword).then(res => {
              lastKeyword.current = keyword;
              return res;
            }),
          );
        }),
      )
      .subscribe();

    return () => sub.unsubscribe();
  }, [debounced, isFocused$, query$, replace]);

  const fetchMore = usePersistFn(async () => {
    await rest.fetchMore(query$.value);
  });

  return {
    ...rest,
    fetchMore,
    replace,
    data,
  };
};
